import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tag } from "antd";
import { CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const sortDayjsList = (dates) =>
	[...(dates || [])]
		.filter(Boolean)
		.sort((a, b) => a.valueOf() - b.valueOf());

const dateKey = (d) => d.format("YYYY-MM-DD");

const eachDayInclusive = (a, b) => {
	const start = a.isBefore(b, "day") ? a : b;
	const end = a.isBefore(b, "day") ? b : a;
	const days = [];
	let cur = start.startOf("day");
	const last = end.startOf("day");
	while (cur.isBefore(last, "day") || cur.isSame(last, "day")) {
		days.push(cur);
		cur = cur.add(1, "day");
	}
	return days;
};

/**
 * 사유서·출장·휴가 등: 해당일 칸 클릭 시 달력 표시.
 * 클릭 = 선택/해제 토글, 클릭&드래그 = 구간 일괄 선택(또는 해제).
 * Pointer events로 모바일 터치 드래그도 지원.
 */
const LeaveMultiDatePicker = ({
	value = [],
	onChange,
	disabledDate,
	disabled = false,
	placeholder = "해당일을 선택하세요",
}) => {
	const inner = value || [];
	const selectedKeys = useMemo(
		() => new Set(inner.map(dateKey)),
		[inner],
	);

	const [open, setOpen] = useState(false);
	const [viewMonth, setViewMonth] = useState(() =>
		inner.length ? inner[0] : dayjs(),
	);
	const [dragState, setDragState] = useState(null);
	const rootRef = useRef(null);
	const gridRef = useRef(null);
	const suppressClickRef = useRef(false);
	const innerRef = useRef(inner);
	const disabledDateRef = useRef(disabledDate);
	const dragStateRef = useRef(null);

	innerRef.current = inner;
	disabledDateRef.current = disabledDate;
	dragStateRef.current = dragState;

	const syncToParent = (dates) => {
		if (!onChange) return;
		onChange(sortDayjsList(dates));
	};

	const isDisabled = (d) => {
		const fn = disabledDateRef.current;
		return typeof fn === "function" ? Boolean(fn(d)) : false;
	};

	const applyDragRange = (start, end, mode) => {
		const current = innerRef.current || [];
		const range = eachDayInclusive(start, end).filter(
			(d) => !isDisabled(d),
		);
		if (!range.length) return;
		const rangeKeys = new Set(range.map(dateKey));
		if (mode === "remove") {
			syncToParent(
				current.filter((d) => !rangeKeys.has(dateKey(d))),
			);
			return;
		}
		const nextMap = new Map(current.map((d) => [dateKey(d), d]));
		range.forEach((d) => {
			nextMap.set(dateKey(d), d.startOf("day"));
		});
		syncToParent([...nextMap.values()]);
	};

	const toggleOne = (d) => {
		if (isDisabled(d)) return;
		const current = innerRef.current || [];
		const key = dateKey(d);
		const keys = new Set(current.map(dateKey));
		if (keys.has(key)) {
			syncToParent(current.filter((x) => !x.isSame(d, "day")));
		} else {
			syncToParent([...current, d.startOf("day")]);
		}
	};

	const remove = (d) => {
		if (!onChange || disabled) return;
		onChange(inner.filter((x) => !x.isSame(d, "day")));
	};

	const dayFromPoint = (clientX, clientY) => {
		const el = document.elementFromPoint(clientX, clientY);
		if (!el || !gridRef.current?.contains(el)) return null;
		const btn = el.closest("[data-day-key]");
		if (!btn) return null;
		const key = btn.getAttribute("data-day-key");
		return key ? dayjs(key) : null;
	};

	useEffect(() => {
		if (!open) return undefined;
		const onDocDown = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				setOpen(false);
				setDragState(null);
			}
		};
		const onPointerUp = () => {
			const prev = dragStateRef.current;
			if (!prev) return;
			if (prev.moved) {
				suppressClickRef.current = true;
				applyDragRange(prev.start, prev.end, prev.mode);
			}
			setDragState(null);
		};
		const onPointerMove = (e) => {
			if (!dragStateRef.current) return;
			// 드래그 선택 중 페이지 가로/세로 스크롤 방지 (모바일)
			if (dragStateRef.current.moved || dragStateRef.current.start) {
				e.preventDefault();
			}
			const d = dayFromPoint(e.clientX, e.clientY);
			if (!d || isDisabled(d)) return;
			setDragState((prev) => {
				if (!prev) return null;
				const moved = prev.moved || !prev.start.isSame(d, "day");
				return { ...prev, end: d, moved };
			});
		};
		const onTouchMove = (e) => {
			if (!dragStateRef.current) return;
			e.preventDefault();
		};
		document.addEventListener("pointerdown", onDocDown);
		document.addEventListener("pointerup", onPointerUp);
		document.addEventListener("pointercancel", onPointerUp);
		document.addEventListener("pointermove", onPointerMove, {
			passive: false,
		});
		document.addEventListener("touchmove", onTouchMove, { passive: false });
		return () => {
			document.removeEventListener("pointerdown", onDocDown);
			document.removeEventListener("pointerup", onPointerUp);
			document.removeEventListener("pointercancel", onPointerUp);
			document.removeEventListener("pointermove", onPointerMove);
			document.removeEventListener("touchmove", onTouchMove);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, onChange]);

	const calendarDays = useMemo(() => {
		const start = viewMonth.startOf("month").startOf("week");
		const end = viewMonth.endOf("month").endOf("week");
		const days = [];
		let cur = start;
		while (cur.isBefore(end, "day") || cur.isSame(end, "day")) {
			days.push(cur);
			cur = cur.add(1, "day");
		}
		return days;
	}, [viewMonth]);

	const dragPreviewKeys = useMemo(() => {
		if (!dragState) return null;
		return new Set(
			eachDayInclusive(dragState.start, dragState.end)
				.filter((d) => !isDisabled(d))
				.map(dateKey),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dragState]);

	const isDaySelected = (d) => {
		const key = dateKey(d);
		if (!dragPreviewKeys) return selectedKeys.has(key);
		const inPreview = dragPreviewKeys.has(key);
		if (dragState?.mode === "remove") {
			return selectedKeys.has(key) && !inPreview;
		}
		return selectedKeys.has(key) || inPreview;
	};

	const summaryText = () => {
		if (!inner.length) return placeholder;
		const sorted = sortDayjsList(inner);
		if (sorted.length === 1) return sorted[0].format("YYYY-MM-DD");
		return `${sorted[0].format("YYYY-MM-DD")} 외 ${sorted.length - 1}일`;
	};

	const onDayPointerDown = (e, d) => {
		if (e.button != null && e.button !== 0) return;
		e.preventDefault();
		if (isDisabled(d)) return;
		suppressClickRef.current = false;
		const mode = selectedKeys.has(dateKey(d)) ? "remove" : "add";
		try {
			e.currentTarget.setPointerCapture?.(e.pointerId);
		} catch (_) {
			/* ignore */
		}
		setDragState({
			start: d,
			end: d,
			mode,
			moved: false,
		});
	};

	const onDayClick = (d) => {
		if (suppressClickRef.current) {
			suppressClickRef.current = false;
			return;
		}
		toggleOne(d);
	};

	const isDragging = Boolean(dragState);

	return (
		<div
			className={`utils-leave-multi-date-picker${
				open ? " utils-leave-multi-date-picker--open" : ""
			}${isDragging ? " utils-leave-multi-date-picker--dragging" : ""}`}
			ref={rootRef}>
			<button
				type="button"
				className={`utils-leave-multi-date-picker__trigger${
					open
						? " utils-leave-multi-date-picker__trigger--open"
						: ""
				}${
					!inner.length
						? " utils-leave-multi-date-picker__trigger--empty"
						: ""
				}`}
				disabled={disabled}
				onClick={() => {
					if (disabled) return;
					setOpen((v) => {
						const next = !v;
						if (next && inner.length) {
							setViewMonth(inner[0]);
						} else if (next) {
							setViewMonth(dayjs());
						}
						return next;
					});
				}}>
				<span className="utils-leave-multi-date-picker__trigger-text">
					{summaryText()}
				</span>
				<CalendarOutlined />
			</button>

			{open && (
				<div className="utils-leave-multi-date-picker__panel">
					<div className="utils-leave-multi-date-picker__nav">
						<button
							type="button"
							className="utils-leave-multi-date-picker__nav-btn"
							onClick={() =>
								setViewMonth((m) =>
									m.subtract(1, "month"),
								)
							}
							aria-label="이전 달">
							<LeftOutlined />
						</button>
						<span className="utils-leave-multi-date-picker__nav-title">
							{viewMonth.format("YYYY년 M월")}
						</span>
						<button
							type="button"
							className="utils-leave-multi-date-picker__nav-btn"
							onClick={() =>
								setViewMonth((m) => m.add(1, "month"))
							}
							aria-label="다음 달">
							<RightOutlined />
						</button>
					</div>
					<div className="utils-leave-multi-date-picker__weekdays">
						{WEEKDAYS.map((w) => (
							<span key={w}>{w}</span>
						))}
					</div>
					<div
						className={`utils-leave-multi-date-picker__grid${
							dragState
								? " utils-leave-multi-date-picker__grid--dragging"
								: ""
						}`}
						ref={gridRef}>
						{calendarDays.map((d) => {
							const outside = !d.isSame(viewMonth, "month");
							const dayDisabled = isDisabled(d);
							const selected = isDaySelected(d);
							const today = d.isSame(dayjs(), "day");
							return (
								<button
									key={dateKey(d)}
									type="button"
									data-day-key={dateKey(d)}
									disabled={dayDisabled}
									className={`utils-leave-multi-date-picker__day${
										outside
											? " utils-leave-multi-date-picker__day--outside"
											: ""
									}${
										selected
											? " utils-leave-multi-date-picker__day--selected"
											: ""
									}${
										today
											? " utils-leave-multi-date-picker__day--today"
											: ""
									}${
										dayDisabled
											? " utils-leave-multi-date-picker__day--disabled"
											: ""
									}`}
									onPointerDown={(e) =>
										onDayPointerDown(e, d)
									}
									onClick={() => onDayClick(d)}>
									{d.date()}
								</button>
							);
						})}
					</div>
					<p className="utils-leave-multi-date-picker__hint">
						클릭으로 선택·해제, 드래그로 여러 날짜 선택
					</p>
				</div>
			)}

			<div className="utils-leave-multi-date-picker__selected">
				{sortDayjsList(inner).map((d) => (
					<Tag
						key={dateKey(d)}
						closable={!disabled}
						onClose={() => remove(d)}>
						{d.format("YYYY-MM-DD")}
					</Tag>
				))}
			</div>
		</div>
	);
};

export default LeaveMultiDatePicker;
